// App.js - Simple mobile/desktop split
import React, { useState, useEffect } from 'react';
import { Routes, Route } from 'react-router-dom';
import { AuthProvider } from './components/AuthContext';
import SecurityGate from './components/SecurityGate';
import './App.css';

// Mobile components - regular imports to prevent flash during load
import MobileAuthForms from './components/MobileAuthForms';
import MobileGravityLayout from './components/MobileGravityLayout';
import MobileNewsragLayout from './components/MobileNewsragLayout';
import MobilePredictLayout from './components/MobilePredictLayout';
const ForgotPassword = React.lazy(() => import('./components/ForgotPassword'));
const PasswordResetConfirm = React.lazy(() => import('./components/PasswordResetConfirm'));
const VerifyEmail = React.lazy(() => import('./components/VerifyEmail'));

// Desktop components - lazy load for performance
const OfficeExperience = React.lazy(() => import('./components/OfficeExperience'));
const AuthForms = React.lazy(() => import('./components/AuthForms'));
const GravityLayout = React.lazy(() => import('./components/GravityLayout'));
const NewsragLayout = React.lazy(() => import('./components/NewsragLayout'));
const StockPickerAgent = React.lazy(() => import('./components/StockPickerAgent'));
const PredictLayout = React.lazy(() => import('./components/PredictLayout'));
const MarketTerrainLayout = React.lazy(() => import('./components/MarketTerrainLayout'));

// Simple mobile detection - iPads are always treated as desktop
function isMobileDevice() {
  const userAgent = navigator.userAgent;

  // Treat iPads as desktop devices (they have large enough screens)
  // Detects both normal iPad user agent and "Request Desktop Website" mode
  const isIPad = /iPad/i.test(userAgent) ||
                 (userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);

  if (isIPad) {
    return false; // Always treat iPad as desktop, regardless of viewport size
  }

  const isMobileWidth = window.innerWidth <= 768;
  const isMobileUserAgent = /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini|Mobile|mobile|CriOS|FxiOS|EdgiOS/i.test(userAgent);
  return isMobileWidth || isMobileUserAgent;
}

// Mobile App Routes
function MobileApp() {
  return (
    <div className="mobile-app">
      <React.Suspense fallback={null}>
        <Routes>
          {/* Mobile React components */}
          <Route path="/auth" element={<MobileAuthForms />} />
          <Route path="/gravity" element={<MobileGravityLayout />} />
          <Route path="/newsrag" element={<MobileNewsragLayout />} />
          <Route path="/predict" element={<MobilePredictLayout />} />
          <Route path="/stock-picker" element={
            <SecurityGate requireDesktop={false} requireAuth={true}>
              <StockPickerAgent />
            </SecurityGate>
          } />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password/:uidb64/:token" element={<PasswordResetConfirm />} />
          <Route path="/verify-email/:uidb64/:token" element={<VerifyEmail />} />

          {/* All other routes redirect to mobile-home.html */}
          <Route path="*" element={<RedirectToMobileHome />} />
        </Routes>
      </React.Suspense>
    </div>
  );
}

// Helper component to redirect to mobile-home.html
function RedirectToMobileHome() {
  useEffect(() => {
    window.location.replace('/mobile-home.html');
  }, []);
  return null;
}

// Desktop App Routes
function DesktopApp() {
  return (
    <div className="desktop-app">
      <React.Suspense fallback={
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#f5f5f5',
          fontFamily: "'Menlo', 'Consolas', 'Monaco', 'Courier New', monospace",
          fontSize: '1.2rem',
          color: '#666'
        }}>
          Loading...
        </div>
      }>
        <Routes>
          <Route path="/" element={
            <SecurityGate requireDesktop={true} requireAuth={false}>
              <OfficeExperience />
            </SecurityGate>
          } />

          <Route path="/auth" element={
            <SecurityGate requireDesktop={true} requireAuth={false}>
              <AuthForms onClose={() => window.history.back()} />
            </SecurityGate>
          } />

          <Route path="/forgot-password" element={
            <SecurityGate requireDesktop={true} requireAuth={false}>
              <ForgotPassword />
            </SecurityGate>
          } />

          <Route path="/reset-password/:uidb64/:token" element={
            <SecurityGate requireDesktop={true} requireAuth={false}>
              <PasswordResetConfirm />
            </SecurityGate>
          } />

          <Route path="/verify-email/:uidb64/:token" element={
            <SecurityGate requireDesktop={true} requireAuth={false}>
              <VerifyEmail />
            </SecurityGate>
          } />

          <Route path="/gravity_view" element={
            <SecurityGate requireDesktop={true} requireAuth={true}>
              <GravityLayout />
            </SecurityGate>
          } />

          <Route path="/terrain_view" element={
            <SecurityGate requireDesktop={true} requireAuth={true}>
              <MarketTerrainLayout />
            </SecurityGate>
          } />

          <Route path="/newsrag_view" element={
            <SecurityGate requireDesktop={true} requireAuth={true}>
              <NewsragLayout onBackToOffice={() => window.history.back()} />
            </SecurityGate>
          } />

          <Route path="/predict_view" element={
            <SecurityGate requireDesktop={true} requireAuth={true}>
              <PredictLayout />
            </SecurityGate>
          } />

          <Route path="/stock-picker" element={
            <SecurityGate requireDesktop={false} requireAuth={true}>
              <StockPickerAgent />
            </SecurityGate>
          } />

          {/* Static pages redirect */}
          <Route path="/about" element={<RedirectToStatic path="/about.html" />} />
          <Route path="/blog" element={<RedirectToStatic path="/blog.html" />} />
          <Route path="/gravity" element={<RedirectToStatic path="/gravity.html" />} />
          <Route path="/newsrag" element={<MobileNewsragLayout />} />
        </Routes>
      </React.Suspense>
    </div>
  );
}

// Helper component to redirect to static pages
function RedirectToStatic({ path }) {
  useEffect(() => {
    window.location.replace(path);
  }, [path]);
  return null;
}

// Main App Component
function App() {
  const [isMobile, setIsMobile] = useState(isMobileDevice());

  // Update mobile state on resize
  useEffect(() => {
    function handleResize() {
      setIsMobile(isMobileDevice());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <AuthProvider>
      {isMobile ? <MobileApp /> : <DesktopApp />}
    </AuthProvider>
  );
}

export default App;
